<template>
    <div class="mb-2">
        <jet-label value="Delivery Schedule" />

        <div class="border border-gray-200 rounded px-3 py-2 mb-2">
            <div
                v-if="form.delivery_schedules?.length"
                class="flex gap-x-3 items-end mb-2"
            >
                <div class="schedule-days-col">
                    <jet-label value="Delivery Days" class="mb-0" />
                </div>
                <div class="schedule-window-col">
                    <jet-label value="Delivery Window" class="mb-0" />
                </div>
                <div class="w-8 shrink-0" aria-hidden="true"></div>
            </div>

            <div
                v-for="(schedule, index) in form.delivery_schedules"
                :key="index"
                class="delivery-schedule-row"
                :class="{ 'mt-3': index > 0 }"
            >
                <div class="flex gap-x-3 items-center">
                    <div class="schedule-days-col">
                        <multiselect
                            :id="'delivery_days_' + index"
                            v-model="schedule.delivery_days"
                            :options="dayOptionsForRow(index)"
                            mode="multiple"
                            valueProp="value"
                            label="label"
                            :closeOnSelect="false"
                            :object="false"
                            placeholder="Select days"
                            :classes="{multipleLabel: 'w-full px-2 whitespace-nowrap'}"
                            class="multi-lg"
                        >
                            <template v-slot:multiplelabel="{ values }">
                                <div class="multiselect-multiple-label whitespace-nowrap">
                                    {{ values?.length }} day{{ values?.length > 1 ? 's' : ''}}
                                </div>
                            </template>
                            <template v-slot:option="{ option }">
                                <div>
                                    {{ option.label }}
                                    <span v-if="option.disabled" class="text-xs text-gray-400 ml-1">In another schedule</span>
                                </div>
                            </template>
                        </multiselect>
                    </div>

                    <div class="schedule-window-col flex items-center gap-x-1">
                        <div class="flex flex-1 min-w-0 items-center gap-x-0.5">
                            <jet-input
                                type="time"
                                v-model="schedule.delivery_window_start"
                                class="w-full min-w-0 px-1.5"
                                :id="'delivery_window_start_' + index"
                            />
                            <button
                                v-if="schedule.delivery_window_start"
                                type="button"
                                class="schedule-time-clear"
                                aria-label="Clear start time"
                                title="Clear start time"
                                @click="clearWindowTime(schedule, 'delivery_window_start')"
                            >
                                ×
                            </button>
                        </div>
                        <span class="text-sm text-gray-500 shrink-0">to</span>
                        <div class="flex flex-1 min-w-0 items-center gap-x-0.5">
                            <jet-input
                                type="time"
                                v-model="schedule.delivery_window_end"
                                class="w-full min-w-0 px-1.5"
                                :id="'delivery_window_end_' + index"
                            />
                            <button
                                v-if="schedule.delivery_window_end"
                                type="button"
                                class="schedule-time-clear"
                                aria-label="Clear end time"
                                title="Clear end time"
                                @click="clearWindowTime(schedule, 'delivery_window_end')"
                            >
                                ×
                            </button>
                        </div>
                    </div>

                    <button
                        v-if="form.delivery_schedules.length > 1"
                        type="button"
                        class="w-8 shrink-0 flex items-center justify-center p-1 rounded hover:bg-gray-100"
                        title="Remove"
                        @click="removeScheduleRow(index)"
                    >
                        <img src="/images/icons/trash.svg" width="16" height="16" alt="Remove" />
                    </button>
                    <div v-else class="w-8 shrink-0" aria-hidden="true"></div>
                </div>

                <div
                    v-show="sortedDays(schedule.delivery_days).length"
                    class="text-xs text-gray-600 mt-1"
                >
                    <span v-for="(day, dayIndex) in sortedDays(schedule.delivery_days)" :key="day">
                        {{ day }}{{ dayIndex === sortedDays(schedule.delivery_days).length - 1 ? '' : ', ' }}
                    </span>
                </div>

                <jet-input-error
                    :message="scheduleRowError(index, 'delivery_days')"
                    class="mt-1"
                />
                <jet-input-error
                    :message="scheduleRowError(index, 'delivery_window_start')"
                    class="mt-1"
                />
                <jet-input-error
                    :message="scheduleRowError(index, 'delivery_window_end')"
                    class="mt-1"
                />
            </div>
        </div>

        <p class="text-xs text-gray-500 mb-2">
            Each day can be in one schedule only. Set a start, an end, or both. Leave both blank to allow delivery any time that day.
        </p>

        <jet-button
            v-if="canAddScheduleRow"
            type="button"
            class="mb-2"
            @click="addScheduleRow"
        >
            Add Delivery Days
        </jet-button>

        <jet-input-error :message="$page.props.errors.delivery_schedules" class="mt-1" />
        <jet-input-error :message="$page.props.errors.auth_code" class="mt-1" />

        <jet-dialog-modal :show="showAuthCodeModal" @close="cancelAuthCode">
            <template #title>
                <h3 class="text-2xl">Authorize Delivery Window Code</h3>
            </template>

            <template #content>
                <form @submit.prevent="confirmAuthCode">
                    <div class="mb-2">
                        <jet-label value="Authorization Code:" class="mb-0" />
                        <jet-input v-model="authCodeInput" type="text" class="w-full" required />
                        <jet-input-error :message="authCodeError" class="mt-1" />
                    </div>
                    <input type="submit" class="hidden" ref="submitAuthCode" />
                </form>
            </template>

            <template #footer>
                <jet-button class="mr-2" @click="$refs.submitAuthCode.click()">
                    Authorize
                </jet-button>
                <jet-tertiary-button @click="cancelAuthCode">
                    Cancel
                </jet-tertiary-button>
            </template>
        </jet-dialog-modal>
    </div>
</template>

<script>
import JetLabel from '@/Jetstream/Label';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError.vue';
import JetButton from '@/Jetstream/Button';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetDialogModal from '@/Jetstream/DialogModal';
import Multiselect from '@vueform/multiselect';
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function emptyScheduleRow() {
    return {
        delivery_days: [],
        delivery_window_start: null,
        delivery_window_end: null,
    };
}

function normalizeTime(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const str = String(value).trim();
    if (!str) {
        return null;
    }
    return str.length >= 5 ? str.slice(0, 5) : str;
}

function sortedDays(days) {
    const selected = Array.isArray(days) ? days : [];
    return WEEKDAYS.filter((day) => selected.includes(day));
}

function cloneSchedules(schedules) {
    return (schedules || []).map((row) => ({
        delivery_days: sortedDays(row.delivery_days),
        delivery_window_start: normalizeTime(row.delivery_window_start),
        delivery_window_end: normalizeTime(row.delivery_window_end),
    }));
}

function windowByDayMap(schedules) {
    const map = {};
    (schedules || []).forEach((row) => {
        sortedDays(row.delivery_days).forEach((day) => {
            map[day] = {
                start: normalizeTime(row.delivery_window_start),
                end: normalizeTime(row.delivery_window_end),
            };
        });
    });
    return map;
}

export default {
    props: ['form', 'deliveryOptions'],

    components: {
        JetLabel,
        JetInput,
        JetInputError,
        JetButton,
        JetTertiaryButton,
        JetDialogModal,
        Multiselect,
    },

    data() {
        return {
            originalSchedules: [],
            showAuthCodeModal: false,
            authCodeInput: null,
            authCodeError: null,
            pendingSubmitCallback: null,
        };
    },

    computed: {
        assignedDays() {
            const days = [];
            (this.form.delivery_schedules || []).forEach((row) => {
                (row.delivery_days || []).forEach((day) => days.push(day));
            });
            return days;
        },
        canAddScheduleRow() {
            return this.assignedDays.length < WEEKDAYS.length;
        },
    },

    watch: {
        'form.delivery_schedules': {
            deep: true,
            handler(rows) {
                (rows || []).forEach((row) => {
                    if (!Array.isArray(row.delivery_days)) {
                        return;
                    }
                    const ordered = sortedDays(row.delivery_days);
                    const same = ordered.length === row.delivery_days.length
                        && ordered.every((day, index) => day === row.delivery_days[index]);
                    if (!same) {
                        row.delivery_days = ordered;
                    }
                });
            },
        },
    },

    mounted() {
        if (!Array.isArray(this.form.delivery_schedules) || this.form.delivery_schedules.length === 0) {
            this.form.delivery_schedules = [emptyScheduleRow()];
        }

        this.form.delivery_schedules = this.form.delivery_schedules.map((row) => ({
            delivery_days: Array.isArray(row.delivery_days)
                ? [...row.delivery_days]
                : String(row.delivery_days || '').split(',').map((d) => d.trim()).filter(Boolean),
            delivery_window_start: normalizeTime(row.delivery_window_start),
            delivery_window_end: normalizeTime(row.delivery_window_end),
        }));

        if (this.form.auth_code === undefined) {
            this.form.auth_code = null;
        }

        this.originalSchedules = cloneSchedules(this.form.delivery_schedules);
    },

    methods: {
        dayOptionsForRow(rowIndex) {
            const usedElsewhere = new Set();
            (this.form.delivery_schedules || []).forEach((row, index) => {
                if (index === rowIndex) {
                    return;
                }
                (row.delivery_days || []).forEach((day) => usedElsewhere.add(day));
            });

            const options = this.deliveryOptions?.length ? this.deliveryOptions : WEEKDAYS;

            return options.map((day) => {
                const value = typeof day === 'string' ? day : day.value;
                const disabled = usedElsewhere.has(value);
                return { value, label: value, disabled };
            });
        },

        sortedDays(days) {
            return sortedDays(days);
        },

        scheduleRowError(index, field) {
            const errors = this.$page.props.errors || {};
            return errors[`delivery_schedules.${index}.${field}`] || null;
        },

        addScheduleRow() {
            if (!this.canAddScheduleRow) {
                return;
            }
            this.form.delivery_schedules.push(emptyScheduleRow());
        },

        removeScheduleRow(index) {
            if (this.form.delivery_schedules.length <= 1) {
                return;
            }
            this.form.delivery_schedules.splice(index, 1);
        },

        /**
         * Clear one side of a delivery window so it can be left blank.
         *
         * @param {object} schedule
         * @param {'delivery_window_start'|'delivery_window_end'} field
         */
        clearWindowTime(schedule, field) {
            schedule[field] = null;
        },

        windowsChanged() {
            const original = windowByDayMap(this.originalSchedules);
            const current = windowByDayMap(this.form.delivery_schedules);

            for (const day of Object.keys(current)) {
                const oldWin = original[day] || { start: null, end: null };
                const newWin = current[day];
                if (oldWin.start !== newWin.start || oldWin.end !== newWin.end) {
                    return true;
                }
            }

            return false;
        },

        requiresAuthCode() {
            return this.windowsChanged();
        },

        /**
         * Ask for the window authorization code before the form is submitted.
         * Day-only changes submit immediately.
         */
        ensureAuthCode(onReady) {
            this.pendingSubmitCallback = onReady;

            const hasCode = this.form.auth_code && String(this.form.auth_code).trim();
            if (this.requiresAuthCode() && !hasCode) {
                this.authCodeInput = null;
                this.authCodeError = null;
                this.showAuthCodeModal = true;
                return;
            }

            onReady();
        },

        /**
         * Open the auth modal after a failed save that required authorization.
         */
        promptAuthForRetry() {
            if (!this.requiresAuthCode()) {
                return;
            }

            this.authCodeInput = null;
            this.authCodeError = this.$page.props.errors?.auth_code || null;
            this.showAuthCodeModal = true;
        },

        confirmAuthCode() {
            const code = this.authCodeInput ? String(this.authCodeInput).trim() : '';
            if (!code) {
                this.authCodeError = 'An authorization code is required.';
                return;
            }

            this.form.auth_code = code;
            this.showAuthCodeModal = false;
            const cb = this.pendingSubmitCallback;
            this.authCodeInput = null;
            this.authCodeError = null;
            if (typeof cb === 'function') {
                cb();
            }
        },

        cancelAuthCode() {
            this.showAuthCodeModal = false;
            this.authCodeInput = null;
            this.authCodeError = null;
            this.form.auth_code = null;
        },
    },
}
</script>

<style scoped>
.schedule-days-col {
    width: 11rem;
    min-width: 9.5rem;
    flex-shrink: 0;
}

.schedule-window-col {
    flex: 1 1 0;
    min-width: 0;
}

.schedule-time-clear {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    line-height: 1;
    padding: 0;
    border: none;
    border-radius: 0.25rem;
    color: #6b7280;
    font-size: 0.875rem;
    background: transparent;
    cursor: pointer;
}

.schedule-time-clear:hover {
    color: #111827;
    background: #f3f4f6;
}
</style>
